// 9fbef606107a605d69c0edbcd8029e5d
/* eslint-disable react/jsx-filename-extension */
import PropTypes from 'prop-types';
import has from 'lodash/has';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { routerPush, getViewportInfo } from '@tcp/core/src/utils';
import { updateLocalStorageData } from '../../../../common/molecules/SearchBar/userRecentStore';
import {
  featureBrandAttribute,
  featureBrandOldAttribute,
  PRODUCTS_PER_PAGE_DESKTOP,
  PRODUCTS_PER_PAGE_MOBILE,
  PRODUCTS_PER_PAGE_TABLET,
} from './SearchDetail.constants';
import { SLASH_C_PATH, SLASH_C_ROUTE } from '../../../../../constants/plp.constants';
import {
  getProductsPerPageDesktop,
  getProductsPerPageMobile,
  getProductsPerPageTablet,
  isShopThisStoreModeOn,
} from '../../../../../reduxStore/selectors/session.selectors';
import { getStoreFilterData } from '../../ShopThisStore/container/ShopThisStore.utils';
import { isClient } from '../../../../../utils/utils';

export const isSearched = () => {
  return true;
};

export const getLatestSearchResultsExists = (latestSearchResults) => {
  return !!(latestSearchResults && latestSearchResults.length > 0);
};

export const setDataInLocalStorage = (searchText, url) => {
  updateLocalStorageData(searchText, url);
};

export const redirectToSuggestedUrl = (searchText, url) => {
  if (searchText) {
    setDataInLocalStorage(searchText, url);
    if (url) {
      routerPush(`${SLASH_C_PATH}cid=${url.split(SLASH_C_ROUTE)[1]}`, `${url}`, { shallow: false });
    } else if (isShopThisStoreModeOn()) {
      routerPush(
        `/search?searchQuery=${searchText}&bopisStoreId=${encodeURIComponent(
          getStoreFilterData()
        )}&mode=shop-this-store`,
        `/search/${searchText}?bopisStoreId=${encodeURIComponent(
          getStoreFilterData()
        )}&mode=shop-this-store`,
        { shallow: true }
      );
    } else {
      routerPush(`/search?searchQuery=${searchText}`, `/search/${searchText}`, { shallow: true });
    }
  }
};

export const getProductsPerPageCount = (state) => {
  const { isTablet = false, isDesktop = false } = getViewportInfo() || {};
  if (isDesktop) {
    return getProductsPerPageDesktop(state) || PRODUCTS_PER_PAGE_DESKTOP;
  }
  if (isTablet) {
    return getProductsPerPageTablet(state) || PRODUCTS_PER_PAGE_TABLET;
  }
  return getProductsPerPageMobile(state) || PRODUCTS_PER_PAGE_MOBILE;
};

export const getSelectedPageProductRange = (limitPerPage, pageNumber, productCount) => {
  return {
    from: (pageNumber - 1) * limitPerPage + 1,
    till: (pageNumber - 1) * limitPerPage + productCount,
  };
};

export const enteredSearchText = () => {
  let enteredText;
  if (typeof window !== 'undefined') {
    enteredText = window?.location?.href;
  }
  if (isShopThisStoreModeOn()) {
    enteredText = enteredText?.split('/search/shop-this-store/')?.[1];
  } else {
    enteredText = enteredText?.split('/search/')?.[1];
  }

  if (enteredText?.split('?')) {
    enteredText = enteredText?.split('?')?.[0];
  }
  if (enteredText) {
    enteredText = decodeURI(enteredText);
  }
  if (!enteredText && isShopThisStoreModeOn()) {
    return '*';
  }

  return enteredText;
};

export const getSearchText = (searchedText) => {
  const searchText = searchedText?.split('?');
  if (searchText) {
    return searchText?.[0];
  }
  return searchedText;
};

export const didYouMeanCheck = (
  isDidYouMeanEnabled,
  searchResultSuggestionsArg,
  oldSearchResultSuggestions
) => {
  return !isDidYouMeanEnabled ? searchResultSuggestionsArg : oldSearchResultSuggestions;
};

export const getCurrentFeatureBrandAttribute = (facets) => {
  if (Array.isArray(facets) && facets.length) {
    if (facets.includes(featureBrandAttribute)) return featureBrandAttribute;
    return featureBrandOldAttribute;
  }
  if (has(facets, featureBrandAttribute)) return featureBrandAttribute;
  if (isEmpty(facets)) return featureBrandAttribute;
  return featureBrandOldAttribute;
};

export function isFeatureBrandPage(router, killSwitch) {
  if (!killSwitch) return false;
  const { query, asPath } = router || {};
  if (typeof query === 'object' && has(query, 'brand')) {
    return has(query, 'brand');
  }
  return asPath?.includes('featurebrand');
}

export const getFeatureBrand = (router = {}, killSwitch = false) => {
  const isFeatureBrand = isFeatureBrandPage(router, killSwitch);
  if (isFeatureBrand) {
    const { query, asPath } = router;
    let featureBrand = get(query, 'brand');
    if (!featureBrand) {
      const [, , , brandQuery] = asPath?.split('/');
      if (brandQuery?.includes('?')) {
        const [brand] = brandQuery?.split('?');
        featureBrand = brand;
      } else featureBrand = brandQuery;
    }
    return decodeURIComponent(featureBrand);
  }
  if (
    isClient() &&
    window &&
    window.location &&
    window?.location?.pathname?.includes('featurebrand')
  ) {
    const [, , , brandQuery] = window?.location?.pathname?.split('/');
    if (brandQuery.includes('?')) {
      const [brand] = brandQuery?.split('?');
      return decodeURIComponent(brand);
    }
    return decodeURIComponent(brandQuery);
  }
  return '';
};

export const getFeatureBrandQuery = (router, facets) => {
  const isFeatureBrand = isFeatureBrandPage(router, true);
  if (isFeatureBrand) {
    const { asPath } = router;
    if (asPath.includes('?'))
      return asPath?.split('?').map((val, idx) => {
        if (idx === 0) {
          return `${val}*`;
        }
        return `?${getCurrentFeatureBrandAttribute(facets)}=${encodeURIComponent(
          getFeatureBrand(router, true)
        )}&${val}`;
      });
    return '';
  }
  return '';
};

export function getSearchQueryForFeatureBrand(router, killSwitch, facets) {
  const featureBrandQuery = getFeatureBrandQuery(router, facets);
  if (featureBrandQuery) return featureBrandQuery.join('');
  return `*?${getCurrentFeatureBrandAttribute(facets)}=${encodeURIComponent(
    getFeatureBrand(router, killSwitch)
  )}`;
}

export const getQueryString = (isFeatureBrand, router, asPath, killSwitch, facets) => {
  if (!isFeatureBrand) {
    return asPath.split('?');
  }
  return getSearchQueryForFeatureBrand(router, killSwitch, facets).split('?');
};

export const SearchListingViewPropTypes = {
  className: PropTypes.string,
  filters: PropTypes.shape({}),
  filtersLength: PropTypes.shape({}),
  formValues: PropTypes.shape({
    sort: PropTypes.string.isRequired,
  }).isRequired,
  getProducts: PropTypes.func.isRequired,
  getMoreProducts: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({}),
  labelsFilter: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
  onSubmit: PropTypes.func.isRequired,
  productsBlock: PropTypes.arrayOf(PropTypes.shape({})),
  products: PropTypes.arrayOf(PropTypes.shape({})),
  labels: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
  plpLabels: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
  totalProductsCount: PropTypes.number,
  searchedText: PropTypes.string,
  slpLabels: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
  sortLabels: PropTypes.arrayOf(PropTypes.shape({})),
  currencyAttributes: PropTypes.shape({}),
  currency: PropTypes.string,
  onAddItemToFavorites: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  isSearchListing: PropTypes.bool,
  asPathVal: PropTypes.string,
  searchResultSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      suggestion: PropTypes.string.isRequired,
    })
  ),
  AddToFavoriteErrorMsg: PropTypes.string,
  removeAddToFavoritesErrorMsg: PropTypes.func,
  trackPageLoad: PropTypes.func,
  pageNameProp: PropTypes.string,
  pageSectionProp: PropTypes.string,
  pageSubSectionProp: PropTypes.string,
  searchType: PropTypes.string,
  isHidePLPRatings: PropTypes.bool,
  wishList: PropTypes.func.isRequired,
  filterCount: PropTypes.number,
  searchTopPromos: PropTypes.arrayOf(
    PropTypes.shape({
      // Only including the most important property
      moduleName: PropTypes.string,
    })
  ),
  accessibilityLabels: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
  isPlcc: PropTypes.bool,
  onSetLastDeletedItemIdAction: PropTypes.func,
  deleteFavItemInProgressFlag: PropTypes.bool,
  resetSlpScrollState: PropTypes.func.isRequired,
  shouldSlpScrollState: PropTypes.bool,
  isPlpPdpAnchoringEnabled: PropTypes.bool,
  isDynamicBadgeEnabled: PropTypes.shape({}),
  isABTestForStickyFilter: PropTypes.bool,
  isPromoApplied: PropTypes.bool,
  isBopisFilterEnabled: PropTypes.bool,
  isSlpSsr: PropTypes.bool,
  isHeaderSticky: PropTypes.bool,
  acrossBrandApiCalled: PropTypes.bool,
};

export const SearchListingViewDefaultProps = {
  className: '',
  filters: {},
  filtersLength: {},
  initialValues: {},
  labelsFilter: {},
  products: [],
  productsBlock: [],
  labels: {},
  plpLabels: {},
  totalProductsCount: 0,
  searchedText: '',
  slpLabels: {},
  sortLabels: {},
  currencyAttributes: {
    exchangevalue: 1,
  },
  currency: 'USD',
  isLoggedIn: false,
  isLoadingMore: false,
  isSearchListing: true,
  asPathVal: '',
  searchResultSuggestions: [],
  AddToFavoriteErrorMsg: '',
  removeAddToFavoritesErrorMsg: () => {},
  trackPageLoad: () => {},
  pageNameProp: '',
  pageSectionProp: '',
  pageSubSectionProp: '',
  isHidePLPRatings: false,
  searchType: 'keyword',
  filterCount: 0,
  searchTopPromos: [],
  accessibilityLabels: {},
  isPlcc: false,
  onSetLastDeletedItemIdAction: () => {},
  deleteFavItemInProgressFlag: false,
  shouldSlpScrollState: false,
  isPlpPdpAnchoringEnabled: false,
  isDynamicBadgeEnabled: false,
  isABTestForStickyFilter: false,
  isPromoApplied: false,
  isBopisFilterEnabled: false,
  isSlpSsr: false,
  isHeaderSticky: false,
  acrossBrandApiCalled: false,
};
